import React, { useState, useEffect } from 'react';
import { Alert, Card, Col, Form, Row } from 'react-bootstrap';
import FalconCardHeader from 'components/common/FalconCardHeader';
import PropTypes from 'prop-types';
import { FiInfo } from 'react-icons/fi';
import { isEmpty } from 'lodash';
import Model from '../monitoring/Model';
import Select from 'react-select';
import { toast } from 'react-toastify';
import ChartBody from '../table-data/ChartBody';

function SocChart({ data, title }) {
  let variant = data.variant;
  const initialYear = Object.keys(variant)[0];
  const initialMonth = Object.keys(variant[initialYear])[0];

  const [showModal, setShowModal] = useState(false);
  const [selectedYears, setSelectedYears] = useState([
    { value: initialYear, label: initialYear }
  ]);
  const [selectedMonths, setSelectedMonths] = useState([
    { value: initialMonth, label: initialMonth }
  ]);
  const [chartData, setChartData] = useState(
    data.data[initialYear][initialMonth]
  );

  useEffect(() => {
    if (selectedYears.length !== 0) {
      const availableMonths = Object.keys(data.data[selectedYears[0].value]);

      // Check if all selected months are available for the selected year(s)
      const validSelectedMonths = selectedMonths.filter(month =>
        availableMonths.includes(month.value)
      );

      if (validSelectedMonths.length !== selectedMonths.length) {
        // If not all selected months are valid, update to the first available month
        setSelectedMonths([
          { value: availableMonths[0], label: availableMonths[0] }
        ]);
      }
    }
  }, [selectedYears, data.data, selectedMonths]);

  useEffect(() => {
    !isEmpty(selectedYears) && !isEmpty(selectedMonths) && mergeChartData();
  }, [selectedYears, selectedMonths]);

  const handleShowModal = () => setShowModal(true);
  const handleCloseModal = () => setShowModal(false);

  const handleYearChange = selectedOptions => {
    if (selectedOptions.length <= 3) {
      setSelectedYears(selectedOptions);
    } else {
      toast.error('You can select only 3 options.');
    }
  };

  const handleMonthChange = selectedOptions => {
    if (selectedOptions.length <= 4) {
      setSelectedMonths(selectedOptions);
    } else {
      toast.error('You can select only 4 options.');
    }
  };

  const mergeChartData = () => {
    const mergedData = { date: [], series: {} };

    selectedYears?.forEach(year => {
      selectedMonths?.forEach(month => {
        const currentData = data.data[year.value]?.[month.value];

        if (currentData) {
          // Merge dates
          mergedData.date = [...mergedData.date, ...currentData.series];

          // Merge series values
          const currentValues = currentData.values;
          Object.keys(currentValues).forEach(key => {
            if (!mergedData.series[key]) {
              mergedData.series[key] = [];
            }
            mergedData.series[key] = [
              ...mergedData.series[key],
              ...currentValues[key]
            ];
          });
        }
      });
    });

    const finalSeries = Object.keys(mergedData.series).map(name => ({
      name,
      data: mergedData.series[name]
    }));

    setChartData({ date: mergedData.date, series: finalSeries });
  };

  const getDisabledYears = () => {
    if (selectedMonths.length === 0) return [];

    const selectedMonthValues = selectedMonths.map(month => month.value);

    // Find years that do not have the selected months
    return Object.keys(variant).filter(year => {
      const monthsInYear = Object.keys(data.data[year]);
      return !selectedMonthValues.some(month => monthsInYear.includes(month));
    });
  };

  const disabledYears = getDisabledYears();

  const yearOptions = Object.keys(variant).map(year => ({
    value: year,
    label: year,
    isDisabled: disabledYears.includes(year) // Disable the year if it does not have the selected month
  }));

  const monthOptions =
    selectedYears.length === 1
      ? variant[selectedYears[0].value]?.map(month => ({
          value: month,
          label: month
        }))
      : [];

  return (
    <Card className="h-100">
      <FalconCardHeader
        title={data?.label}
        titleTag="h5"
        light={true}
        desc={data?.desc}
        infoBtn={
          !isEmpty(data.description) && <FiInfo onClick={handleShowModal} />
        }
      />
      <Card.Body className="h-100 px-3 pt-0" dir="ltr">
        <Row className="d-flex py-2 pb-3 justify-content-end align-item-end">
          <Col lg={3}>
            <Form.Group controlId="yearSelect">
              <Form.Label>Select Years</Form.Label>
              <Select
                isMulti
                value={selectedYears}
                onChange={handleYearChange}
                options={yearOptions}
                closeMenuOnSelect={false}
                isDisabled={selectedMonths.length > 1}
              />
            </Form.Group>
          </Col>
          <Col lg={3}>
            <Form.Group controlId="monthSelect">
              <Form.Label>Select Months</Form.Label>
              <Select
                isMulti
                value={selectedMonths}
                onChange={handleMonthChange}
                options={monthOptions}
                closeMenuOnSelect={false}
                isDisabled={selectedYears.length > 1}
              />
            </Form.Group>
          </Col>
        </Row>

        <ChartBody landCoverChartData={chartData} />
      </Card.Body>
      <Model
        showModal={showModal}
        handleCloseModal={handleCloseModal}
        description={data.description}
        title={data?.label}
      />
    </Card>
  );
}

SocChart.propTypes = {
  data: PropTypes.shape({
    label: PropTypes.string,
    desc: PropTypes.string,
    unit: PropTypes.string,
    description: PropTypes.object,
    data: PropTypes.object.isRequired,
    variant: PropTypes.object
  }).isRequired,
  title: PropTypes.string
};

export default SocChart;
